<template>
  <div class="flex items-center px-2 pb-4 space-x-2">
    <!-- Headline is editable -->
    <template v-if="isEditable">
      <div class="flex items-center space-x-1">
        <h5 class="text-2xl">Medikation</h5>
        <component-info-icon placement="top">
          <template #content>
            <p>
              - Welche AM wenden Sie (davon) aktuell an?
              <br />
              - Wie wenden Sie die AM an?
              <br />
              - Wofür oder wogegen wenden Sie die AM an?
              <br />
              - Wie sinnvoll/hilfreich finden Sie das/die AM?
              <br />
              - Haben Sie die Dosis (gelegentlich) geändert/ausgelassen?
              <br />
              - Welche weiteren AM wurden verordnet oder als Selbstmedikation angewendet?
              <br />
              - Wie häufig haben Sie im letzten Monat vergessen, Ihre AM einzunehmen?
              <br />
              - Haben Sie Probleme/Symptome, die Sie mit dem AM in Zusammenhang bringen?
              <br />
              - Wo/wie lagern Sie Ihre AM?
            </p>
          </template>
        </component-info-icon>
      </div>
    </template>

    <!-- Headline is not editable -->
    <template v-else>
      <h5 class="text-2xl">Medikation</h5>
      <component-button class="p4umc-primary" label="Bearbeiten" :test-id="testId" @click="changeIsEditable" />
    </template>
  </div>

  <!-- Alert for not loadable medications -->
  <component-alert
    v-if="
      (unloadableMedicationsUnsolved && unloadableMedicationsUnsolved.length > 0) ||
      (unloadableMedicationsSolved && unloadableMedicationsSolved.length > 0)
    "
    type="warning"
  >
    <h4 class="font-bold">
      In diesem Bereich befinden sich Arzneimittel bzw. Medizinprodukte, die in der aktualisierten Datenbank ersetzt
      (oder geschlechtsspezifisch zugeordnet) wurden.
    </h4>

    <p class="mt-2">
      Bitte tauschen Sie folgende Arzneimittel bzw. Medizinprodukte durch geeignete Neue aus, damit sie im Check erkannt
      und berücksichtigt werden:
    </p>
    <ul class="mt-2 ml-8 list-disc">
      <template v-if="unloadableMedicationsUnsolved?.length > 0">
        <li v-for="(item, index) in unloadableMedicationsUnsolved" :key="'unsolved-' + index">
          <em>{{ item.fam }}</em>
        </li>
      </template>
      <template v-if="unloadableMedicationsSolved?.length > 0">
        <!-- prettier-ignore -->
        <li v-for="(item, index) in unloadableMedicationsSolved" :key="'solved-' + index">
          <em>{{ item.old.fam }}</em>
          (kann ggf. ersetzt werden durch <em>{{ item.new.Name }}</em>)
        </li>
      </template>
    </ul>
  </component-alert>

  <!-- Table -->
  <section ref="refMedicationBar">
    <template v-if="recordMedications.length > 0">
      <medication-table
        :record-medications="recordMedications"
        :is-editable="isEditable"
        :is-loading="isLoading"
        :test-id="testId"
        @update-medications="updateMedications"
        @cancel-update-medications="cancelUpdateMedications"
      />
    </template>
  </section>
  <!-- Search -->
  <medication-component-pac-search v-if="isEditable" :disabled="isMassUpdateProcessing" :test-id="testId" />

  <!-- Buttons -->
  <div v-if="hasThirdParty('p4u')" class="flex justify-between py-2">
    <button @click.capture="checkInvalidDosageFirst($event)">
      <medication-component-athina-import-export
        :hide-import="!isEditable"
        :disabled="isMassUpdateProcessing"
        @is-open="
          (open) => {
            athinaDialogIsOpen = open;
          }
        "
      />
    </button>

    <template v-if="isEditable && hasServices">
      <medication-component-pharmaceutical-services :services="services" :test-id="testId" />
    </template>

    <button @click.capture="checkInvalidDosageFirst($event)">
      <medication-component-plan-print :disabled="isMassUpdateProcessing" reload-record :test-id="testId" />
    </button>
  </div>

  <!-- Dialogs -->
  <component-core-info-dialog ref="refCoreInfoDialog" :test-id="testId" />
  <medication-component-detail-dialog ref="refEditDetailDialog" :is-editable="isEditable" :test-id="testId" />
  <medication-component-indication-dialog ref="refIndicationDialog" :test-id="testId" />

  <component-dialog
    ref="refInvalidDosageConfirmationDialog"
    dialog-title="Ungültige Medikation"
    dialog-content="Bitte korrigieren Sie die markierten Dosierungsangaben."
    dialog-type="information"
    primary-label="OK"
    :test-id="testId"
  />
</template>

<script>
  import {computed, inject, onMounted, provide, ref, watch} from "vue";
  import {router, usePage} from "@inertiajs/vue3";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentButton from "@components/Buttons/Button.vue";
  import ComponentCoreInfoDialog from "@components/Dialogs/CoreInfoDialog/CoreInfoDialog.vue";
  import ComponentInfoIcon from "@components/Icons/InfoIcon.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";

  import MedicationComponentAthinaImportExport from "@pages/Records/Components/Sections/MedicationBar/Components/AthinaImportExport.vue";
  import MedicationComponentDetailDialog from "@pages/Records/Components/Sections/MedicationBar/Components/DetailDialog/DetailDialog.vue";
  import MedicationComponentIndicationDialog from "@pages/Records/Components/Sections/MedicationBar/Components/Indications/IndicationDialog.vue";
  import MedicationComponentPacSearch from "@pages/Records/Components/Sections/MedicationBar/Components/PacSearch.vue";
  import MedicationComponentPharmaceuticalServices from "@pages/Records/Components/Sections/MedicationBar/Components/PharmaceuticalServices.vue";
  import MedicationComponentPlanPrint from "@pages/Records/Components/Sections/MedicationBar/Components/PlanPrint.vue";
  import MedicationTable from "@pages/Records/Components/Sections/MedicationBar/Components/Table.vue";
  import {cloneDeep, debounce} from "lodash";
  import {delayWhileEditing} from "@utils/Helpers/DelayWhileEditing.js";

  export default {
    name: "RecordComponentSectionMedicationBar",

    components: {
      ComponentDialog,
      ComponentAlert,
      ComponentButton,
      ComponentCoreInfoDialog,
      ComponentInfoIcon,
      MedicationComponentAthinaImportExport,
      MedicationComponentDetailDialog,
      MedicationComponentIndicationDialog,
      MedicationComponentPacSearch,
      MedicationComponentPharmaceuticalServices,
      MedicationComponentPlanPrint,
      MedicationTable,
    },

    props: {
      isEditable: {
        type: Boolean,
        required: true,
      },
      changeIsEditable: {
        type: Function,
        required: true,
      },
      recordData: {
        type: Object,
        required: true,
      },
      unloadableMedicationsUnsolved: {
        type: Object,
        required: true,
      },
      unloadableMedicationsSolved: {
        type: Object,
        required: true,
      },
      hasInvalidDosageFields: {
        type: Boolean,
        default: false,
      },
      isLoading: {
        type: Boolean,
        required: true,
      },
    },

    setup(props) {
      const page = usePage();

      const hasThirdParty = inject("$hasThirdParty");
      const axios = inject("$axios");
      const setIsMassUpdateProcessing = inject("setIsMassUpdateProcessing");
      const getIsMassUpdateProcessing = inject("getIsMassUpdateProcessing");

      const refCoreInfoDialog = ref(null);
      const refEditDetailDialog = ref(null);
      const refIndicationDialog = ref(null);
      const refInvalidDosageConfirmationDialog = ref(null);
      const refMedicationBar = ref(null);

      const recordMedications = ref(cloneDeep(props.recordData.medications));
      const services = ref(null);
      const hasServices = ref(false);
      const athinaDialogIsOpen = ref(false);

      const isMassUpdateProcessing = computed(() => getIsMassUpdateProcessing("medication"));

      onMounted(() => {
        loadServices();
      });

      watch(
        () => props.recordData.medications,
        (newValue, oldValue) => {
          recordMedications.value = cloneDeep(props.recordData.medications);

          // whenever a medication has added or deleted
          // we have to check for available services
          if (newValue.length !== oldValue.length) {
            loadServices();
          }
        },
        {deep: true},
      );

      const openDialog = (dialogName, data) => {
        switch (dialogName) {
          case "refCoreInfoDialog":
            refCoreInfoDialog.value.open(data);
            break;
          case "refEditDetailDialog":
            refEditDetailDialog.value.open(data);
            break;
          case "refIndicationDialog":
            refIndicationDialog.value.open(data);
            break;
        }
      };

      const loadServices = () => {
        axios
          .get(
            route("api.records.pharmaceutical-service", {
              patient: page.props.patient.id,
              record: page.props.record.id,
            }),
          )
          .then((response) => {
            services.value = response.data;
            hasServices.value = services.value.blood_pressure.has || services.value.inhaler.has;
          });
      };

      const updateMedications = () => {
        setIsMassUpdateProcessing("medication", true);
        putMedications();
      };

      const putMedications = debounce(
        () => {
          delayWhileEditing(refMedicationBar, () => {
            router.patch(
              route("medications.mass-update", {
                patient: page.props.patient.id,
                record: page.props.record.id,
              }),
              {
                ...recordMedications.value,
              },
              {
                preserveScroll: true,
                only: ["record", "flash", "errors"],
                onFinish: () => {
                  setIsMassUpdateProcessing("medication", false);
                },
              },
            );
          });
        },
        500,
        {trailing: true},
      );

      const cancelUpdateMedications = () => {
        // cancel debounce
        putMedications.cancel();
        // cancel requests
        router.cancel();
        setIsMassUpdateProcessing("medication", false);
      };

      provide("medicationOpenDialog", openDialog);

      function checkInvalidDosageFirst(event) {
        if (props.hasInvalidDosageFields) {
          if (athinaDialogIsOpen.value) {
            // Athina Dialog is already open, so the error occurred while importing.
          } else {
            // Athina Dialog is not open, so intercept the button click.
            event.stopImmediatePropagation();
            refInvalidDosageConfirmationDialog.value.open();
          }
        }
      }

      return {
        /** inject */
        hasThirdParty,

        /** ref */
        refCoreInfoDialog,
        refEditDetailDialog,
        refIndicationDialog,
        refInvalidDosageConfirmationDialog,
        refMedicationBar,

        /** const */
        recordMedications,
        athinaDialogIsOpen,

        /** computed **/
        hasServices,
        services,
        isMassUpdateProcessing,

        /** function **/
        checkInvalidDosageFirst,
        updateMedications,
        cancelUpdateMedications,
      };
    },
  };
</script>
